/** Frame-rate limit — Accessibility → Frame rate. */

export const FPS_LIMIT_STEPS = [0, 120, 90, 60, 45, 30, 20] as const;

export type FpsLimit = (typeof FPS_LIMIT_STEPS)[number];

const FPS_LIMIT_SET = new Set<number>(FPS_LIMIT_STEPS);

/** Gameplay cap when manual Low Power / battery saver toggle is on (Safari has no LPM API). */
export const LOW_POWER_GAMEPLAY_CAP_FPS = 60 as const;

let currentLimit: FpsLimit = 0;
/** User enables in Settings when device Low Power Mode is on — no reliable web auto-detect on iOS. */
let lowPowerCap60Enabled = false;

export function readLowPowerCap60Enabled(raw: string | null | undefined): boolean {
    return raw === "1" || raw === "true";
}

export function setLowPowerCap60Enabled(on: boolean): void {
    lowPowerCap60Enabled = !!on;
}

export function isLowPowerCap60Enabled(): boolean {
    return lowPowerCap60Enabled;
}

/** User Accessibility limit, optionally capped at 60 when low-power toggle is on. */
export function getEffectiveGameplayFpsLimit(): FpsLimit {
    if (!lowPowerCap60Enabled) return currentLimit;
    if (currentLimit === 0 || currentLimit > LOW_POWER_GAMEPLAY_CAP_FPS) {
        return LOW_POWER_GAMEPLAY_CAP_FPS;
    }
    return currentLimit;
}

export function readFpsLimit(raw: string | null | undefined): FpsLimit {
    const n = Number(raw);
    if (Number.isFinite(n) && FPS_LIMIT_SET.has(n)) return n as FpsLimit;
    return 0;
}

export function fpsLimitToSliderIndex(limit: FpsLimit): number {
    const i = FPS_LIMIT_STEPS.indexOf(limit);
    return i >= 0 ? i : 0;
}

export function sliderIndexToFpsLimit(index: number): FpsLimit {
    const i = Math.max(0, Math.min(FPS_LIMIT_STEPS.length - 1, Math.round(Number(index) || 0)));
    return FPS_LIMIT_STEPS[i];
}

export function formatFpsLimitLabel(limit: FpsLimit): string {
    return limit === 0 ? "Unlimited" : `${limit} FPS`;
}

/** Parse typed Settings value: "Unlimited", "60", "60 FPS", slider index, etc. */
export function parseFpsLimitInput(raw: string | number | null | undefined): FpsLimit | null {
    if (typeof raw === "number" && Number.isFinite(raw)) {
        if (FPS_LIMIT_SET.has(raw)) return raw as FpsLimit;
        if (raw >= 0 && raw < FPS_LIMIT_STEPS.length && Number.isInteger(raw)) {
            return FPS_LIMIT_STEPS[raw];
        }
        return null;
    }
    const trimmed = String(raw ?? "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
    if (!trimmed) return null;
    if (
        trimmed === "unlimited" ||
        trimmed === "ul" ||
        trimmed === "inf" ||
        trimmed === "infinite" ||
        trimmed === "off" ||
        trimmed === "max"
    ) {
        return 0;
    }
    const fpsMatch = /^(\d+)\s*(?:fps)?$/.exec(trimmed);
    if (fpsMatch) {
        const n = Number(fpsMatch[1]);
        if (FPS_LIMIT_SET.has(n)) return n as FpsLimit;
        if (n >= 0 && n < FPS_LIMIT_STEPS.length) return FPS_LIMIT_STEPS[n];
    }
    return null;
}

export function setGameplayFpsLimit(limit: FpsLimit): void {
    currentLimit = limit;
}

export function getGameplayFpsLimit(): FpsLimit {
    return currentLimit;
}

/** Minimum ms between rendered frames; 0 = uncapped. Applies during gameplay only (menu uses separate throttle). */
export function getGameplayFpsMinFrameMs(): number {
    const limit = getEffectiveGameplayFpsLimit();
    return limit > 0 ? 1000 / limit : 0;
}
